/*
 * -----------------------------------------------------------------------
 *  <copyright file="audit-log/detail-grid.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import { useApolloClient } from '@apollo/client/react';
import { Tabs } from '@mui/material';
import Tab from '@mui/material/Tab';
import React, { useEffect, useState } from 'react';
import RecordChangeGrid from './change-grid';
import { AuditRecord, getItemHistory, getPanelsAndTables } from './service';
import { useBoolean } from 'hooks';
import {
    GET_AUDIT_HISTORY_PANELS_AND_DATA_VIEWS,
    GET_AUDIT_HISTORY_PANELS_AND_TABLES,
    GET_AUDIT_HISTORY_PANELS_AND_TABLES_AND_FILTERS,
} from 'queries/audit';
import { AuditDataType } from 'types/graphql-global-types';
import { formatDateUTC_DEPRECATED } from 'utils/convertor';
import Loading from 'views/components/core/loading';
import { panelsAndTablesHistory } from '@queries//generated/audit.generated';

interface Props {
    record: AuditRecord;
    items: AuditRecord[];
    savedItem: AuditRecord[];
    setSavedItem: (id: number | string, item: AuditRecord) => void;
    orgId: string;
    setAdditionalData: (data: panelsAndTablesHistory) => void;
    additionalData: panelsAndTablesHistory | null;
}

const DetailGrid = ({ record, items, savedItem, setSavedItem, orgId, setAdditionalData, additionalData }: Props) => {
    const loadingHistory = useBoolean(false);
    const loading = useBoolean(false);
    const client = useApolloClient();
    const [itemHistory, setItemHistory] = useState<AuditRecord[]>([]);
    const [newItem, setNewItem] = useState<AuditRecord | null>(null);
    const [oldItem, setOldItem] = useState<AuditRecord | null>(null);
    const [oldRecord, setOldRecord] = useState<AuditRecord | null>(null);
    const [newRecord, setNewRecord] = useState<AuditRecord | null>(null);
    const [arrayForTabs, setArrayForTabs] = useState<AuditRecord[]>([]);
    const [active, setActive] = useState(0);
    const [additionalInfo, setAdditionalInfo] = useState<panelsAndTablesHistory | null>(null);

    useEffect(() => {
        if (itemHistory && record) {
            let old = null as any;
            let newElement = null as any;
            itemHistory.forEach((i: any) => {
                if (i?.auditId === record.id || (itemHistory.length === 1 && !i.auditId)) {
                    setNewItem(i);
                    newElement = i;
                    return false;
                }
                if (!newElement) old = i;
            });
            if (record.action !== 'ADD') setOldItem(old);
        }
        if (record) {
            if (JSON.stringify(oldRecord) !== JSON.stringify(JSON.parse(record.before)))
                setOldRecord(JSON.parse(record.before));
            if (JSON.stringify(newRecord) !== JSON.stringify(JSON.parse(record.after))) {
                const parsedObject = JSON.parse(record.after);
                if (record.action === 'DELETE') {
                    parsedObject.Reason = record.reason;
                } else if (record.action === 'RESTORE' && record.reason) parsedObject.RestoreReason = record.reason;

                setNewRecord(parsedObject);
            }
        }
    }, [itemHistory, record]);

    useEffect(() => {
        if (itemHistory) {
            if (newItem)
                setArrayForTabs(
                    [...itemHistory]
                        .filter((i: any) => new Date(i.updated).valueOf() < new Date(newItem?.updated).valueOf())
                        .sort((a: any, b: any) => new Date(b.updated).valueOf() - new Date(a.updated).valueOf())
                        .slice(0, 15),
                );
            // show only 15 last changes
            else
                setArrayForTabs(
                    [...itemHistory]
                        .sort((a: any, b: any) => new Date(b.updated).valueOf() - new Date(a.updated).valueOf())
                        .slice(0, 15),
                ); // show only 15 last changes
        }
    }, [itemHistory, newItem]);

    const getHistory = (query: any) => {
        query
            .then((history: any) => {
                if (history) {
                    const historyArray = history
                        .filter((item: any) => item)
                        .sort((a: any, b: any) => new Date(a.updated).valueOf() - new Date(b.updated).valueOf());
                    setItemHistory(historyArray);
                    setSavedItem(record.itemId, historyArray);
                }
            })
            .catch((e: any) => {
                // eslint-disable-next-line no-console
                console.log('Error loading history: ', e);
            })
            .finally(() => {
                loadingHistory.setFalse();
            });
    };

    useEffect(() => {
        let query = null as any;
        if (record?.data && !savedItem && record.data !== AuditDataType.DASHBOARD) {
            query = getItemHistory({
                client,
                itemId: record.itemId,
                parentId: record.parentId,
                type: record.data,
                originService: record.originService,
                serviceItemId: record.serviceItemId,
            });
        } else if (record?.data && !savedItem && record.data === AuditDataType.DASHBOARD) {
            loading.setTrue();
            query = getItemHistory({
                client,
                itemId: record.itemId,
                parentId: record.parentId,
                type: record.data,
                orgId,
                studyId: record.studyId,
                serviceItemId: record.serviceItemId,
            });
        }
        if (query) {
            loadingHistory.setTrue();
            if (record.data === AuditDataType.DASHBOARD)
                getPanelsAndTables({
                    client,
                    query: GET_AUDIT_HISTORY_PANELS_AND_TABLES,
                    orgId,
                    studyId: record.studyId,
                    timestamp: record.created,
                })
                    .then((data) => {
                        setAdditionalInfo(data);
                        setAdditionalData(data);

                        getHistory(query);
                    })
                    .catch((e: any) => {
                        // eslint-disable-next-line no-console
                        console.log('Error loading panels: ', e);
                    })
                    .finally(() => {
                        loading.setFalse();
                    });
            else if (record.data === AuditDataType.DATA_VIEW_CONFIGURATION)
                getPanelsAndTables({
                    client,
                    query: GET_AUDIT_HISTORY_PANELS_AND_TABLES_AND_FILTERS,
                    orgId,
                    studyId: record.studyId,
                    timestamp: record.created,
                })
                    .then((data) => {
                        setAdditionalInfo(data);
                        setAdditionalData(data);

                        getHistory(query);
                    })
                    .catch((e: any) => {
                        // eslint-disable-next-line no-console
                        console.log('Error loading panels: ', e);
                    })
                    .finally(() => {
                        loading.setFalse();
                    });
            else if (record.data === AuditDataType.CONTROLS_HUB_CONFIG_PANEL) {
                getPanelsAndTables({
                    client,
                    query: GET_AUDIT_HISTORY_PANELS_AND_DATA_VIEWS,
                    orgId,
                    studyId: record.studyId,
                    timestamp: record.created,
                })
                    .then((data) => {
                        setAdditionalInfo(data);
                        setAdditionalData(data);

                        getHistory(query);
                    })
                    .catch((e: any) => {
                        // eslint-disable-next-line no-console
                        console.log('Error loading panels: ', e);
                    })
                    .finally(() => {
                        loading.setFalse();
                    });
            } else getHistory(query);
        } else if (savedItem) setItemHistory(savedItem);
        if (additionalData) setAdditionalInfo(additionalData);
    }, []);

    const getPreviousItem = (historyItem: any) => {
        const index = arrayForTabs.findIndex((item: any) => item.auditId === historyItem.auditId);
        if (index >= 0 && index <= arrayForTabs.length - 1) return arrayForTabs[index + 1]; // the most recent at the start of the array
    };

    if (loadingHistory.value || loading.value) return <Loading inside />;
    return (
        <>
            <Tabs
                className="tab-details-item audit-log"
                value={active}
                onChange={(e: any, value: any) => setActive(value)}
            >
                <Tab className="risk-details-label" label="Changes" data-testid="changes-tab" />
                {arrayForTabs?.length > 0 &&
                    arrayForTabs.map((record, index) => (
                        <Tab
                            className="risk-details-label"
                            key={`${record.updated}-${record.auditId}`}
                            label={formatDateUTC_DEPRECATED(record.updated, true, 'DD-MMM-YYYY HH:mm:ss UTC')}
                            data-testid={`risk-details-tab-${index}`}
                        />
                    ))}
            </Tabs>
            {active === 0 ? (
                <RecordChangeGrid
                    dataType={record.data}
                    oldRecord={oldRecord}
                    newRecord={newRecord}
                    record={record}
                    newItem={newItem}
                    oldItem={oldItem}
                    additionalInfo={additionalInfo}
                />
            ) : null}
            {arrayForTabs.length > 0 &&
                arrayForTabs.map((item: any, index: number) => {
                    const auditRecord = items.find((el: any) => el.id === item.auditId);
                    const oldRecord = auditRecord ? JSON.parse(auditRecord.before) : null;
                    const newRecord = auditRecord ? JSON.parse(auditRecord.after) : null;

                    if (oldRecord?.D === false && newRecord?.D === true) {
                        newRecord.Reason = auditRecord?.reason;
                    }

                    if (active === index + 1) {
                        if (!oldRecord && !newRecord)
                            // Purely system changes with no audit records
                            return <div style={{ paddingTop: 20, paddingBottom: 20 }}>No Records</div>;
                        return (
                            <RecordChangeGrid
                                key={item.updated}
                                dataType={record.data}
                                newItem={item}
                                oldItem={getPreviousItem(item)}
                                record={auditRecord}
                                oldRecord={oldRecord}
                                newRecord={newRecord}
                                additionalInfo={additionalInfo}
                            />
                        );
                    }
                })}
        </>
    );
};

export default React.memo(DetailGrid);
